import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { withStyles } from '@mui/styles'

import { encodeToBase64, isACorrectFormat } from '../../../core/utils'
import { useAjaxContext } from '../../../core/ajax'
import { ThemeButton } from '../../../ui'
import { DEFAULT_FILE_SIZE, API_ENDPOINT } from '../constants'
import { fileUploaderStyles } from './file-uploader.styles'

/**
 * @typedef {object} FileUploadSuccessData
 * @property {any} data - Los datos de respuesta del servidor después de una subida exitosa.
 */

/**
 * @typedef {object} FileUploadErrorData
 * @property {string} mssg - El mensaje de error descriptivo.
 */

/**
 * @typedef {object} FileUploaderProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {string} label - El texto que se muestra en el botón de subida.
 * @property {string[]} accept - Array de extensiones de archivo permitidas (ej. `['.pdf', '.jpg', '.png']`).
 * @property {function(FileUploadSuccessData): void} onSuccess - Callback que se ejecuta cuando el archivo se sube exitosamente. Recibe los datos de respuesta del servidor.
 * @property {function(FileUploadErrorData): void} onError - Callback que se ejecuta cuando ocurre un error durante la subida. Recibe un objeto con el mensaje de error.
 * @property {boolean} [disabled] - Si es `true`, deshabilita el botón de subida.
 * @property {number} [maxSize=DEFAULT_FILE_SIZE] - Tamaño máximo permitido del archivo en bytes.
 * @property {string} [type='certificados'] - El tipo de documento que se está subiendo, usado para categorización en el backend.
 * @property {string | number} applicationId - El ID de la solicitud a la que se asociará el documento.
 * @property {string | number} [budgetId] - El ID del presupuesto asociado (opcional).
 */

/**
 * Un componente de subida de archivos que codifica archivos en base64 y los envía a un endpoint API.
 * 
 * Este componente maneja el flujo completo de subida de archivos:
 * - **Validación de formato**: Verifica que el archivo tenga una extensión permitida
 * - **Validación de tamaño**: Comprueba que el archivo no exceda el tamaño máximo
 * - **Codificación base64**: Convierte el archivo a base64 antes de enviarlo
 * - **Envío al servidor**: Realiza una petición POST con los datos del archivo y metadatos
 * - **Gestión de estados**: Muestra un indicador de carga durante la subida
 * - **Manejo de errores**: Proporciona mensajes de error descriptivos
 * 
 * El componente está integrado con Redux para obtener el ID del usuario actual y utiliza
 * el contexto de AJAX para realizar las peticiones HTTP.
 * 
 * @component
 * @example
 * const MyDocumentUploader = () => {
 *   const handleSuccess = (data) => {
 *     console.log('Archivo subido:', data);
 *     showNotification('Archivo subido correctamente');
 *   };
 * 
 *   const handleError = (error) => {
 *     console.error('Error:', error.mssg);
 *     showNotification(error.mssg, 'error');
 *   };
 * 
 *   return (
 *     <FileUploader
 *       label="Subir Certificado"
 *       accept={['.pdf', '.jpg', '.png']}
 *       maxSize={5 * 1024 * 1024} // 5MB
 *       type="certificados"
 *       applicationId={123}
 *       budgetId={456}
 *       onSuccess={handleSuccess}
 *       onError={handleError}
 *       color="primary"
 *     />
 *   );
 * };
 * 
 * @param {FileUploaderProps} props - Las props del componente FileUploader.
 */
export const FileUploader = withStyles(fileUploaderStyles)(({
  classes,
  label,
  accept,
  onSuccess,
  onError,
  disabled,
  maxSize = DEFAULT_FILE_SIZE,
  type = 'certificados',
  applicationId,
  budgetId,
  ...props
}) => {
  const { post } = useAjaxContext()
  const userId = useSelector((state) => state.global.authData.user_id)
  const [loading, setLoading] = useState(false)

  const handleChangeUpload = async (e) => {
    setLoading(true)
    const file = e.target.files[0]
    if (file) {
      if (accept && accept.length > 0 && !isACorrectFormat(file.name, accept)) {
        setLoading(false)
        return onError({ mssg: 'Archivo con formato no válido' })
      } else if (file.size > maxSize) {
        setLoading(false)
        return onError({ mssg: 'Excede el tamaño máximo' })
      }
      const mockErrorData = {
        mssg: 'Problema con la subida del fichero',
      }
      postDocument(file)
        .then(({ data }) => {
          setLoading(false)
          return onSuccess(data)
        })
        .catch(({ response }) => {
          setLoading(false)
          return onError(response ? response.data : mockErrorData)
        })
    } else setLoading(false)
  }

  const postDocument = async (file) => {
    const base64File = await encodeToBase64(file)
    let payload = {
      documento: base64File,
      nombre: file.name.replace(/\.[^/.]+$/, ''),
      tipo: type,
      extension: file.type.split('/')[1],
      cliente_id: parseInt(userId),
      solicitud_id: applicationId.toString(),
    }
    if (budgetId) {
      payload = { ...payload, presupuesto_id: budgetId.toString() }
    }
    return post(API_ENDPOINT, payload)
  }

  return (
    <div>
      <input
        id={`file-upload-${applicationId}-${type}`}
        type="file"
        accept={accept.join(',')}
        className={classes.input}
        onChange={handleChangeUpload}
        disabled={disabled}
      />
      <label htmlFor={`file-upload-${applicationId}-${type}`}>
        <ThemeButton component="span" {...{ loading, disabled, ...props }}>
          {label}
        </ThemeButton>
      </label>
    </div>
  )
})
